import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import swal from 'sweetalert';
import { getGroupTours, deleteGroupTour } from '../../api/groupToursApi';
import { Pagination } from 'semantic-ui-react';

const EMPTY_MSG = 'No services yet. Click Add to create your first one.';
const PER_PAGE = 20;

export default function PartnerMyGroupTrips() {
  const history = useHistory();
  const location = useLocation();
  const filtersRef = useRef({ search: '', date_from: '', date_to: '', status: '', published: '' });
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    date_from: '',
    date_to: '',
    status: '',
    published: ''
  });
  const [sortBy, setSortBy] = useState('start_date');
  const [sortOrder, setSortOrder] = useState('asc');

  filtersRef.current = filters;

  const fetchList = useCallback((params = {}) => {
    setLoading(true);
    setError(false);
    const f = { ...filtersRef.current, ...params.filtersOverride };
    const page = params.page || new URLSearchParams(location.search).get('page') || 1;
    const q = {
      created_by_me: 'true',
      per_page: PER_PAGE,
      page,
      ...(f.search ? { 'q[name_cont]': f.search } : {}),
      ...(f.date_from ? { date_from: f.date_from } : {}),
      ...(f.date_to ? { date_to: f.date_to } : {}),
      ...(f.status ? { status: f.status } : {}),
      ...(f.published ? { published: f.published } : {}),
      sort_by: params.sort_by ?? sortBy,
      sort_order: params.sort_order ?? sortOrder,
      ...params
    };
    delete q.filtersOverride;
    getGroupTours(q)
      .then((res) => {
        const raw = (res.data && res.data.group_tours) || res.data || [];
        setList(Array.isArray(raw) ? raw : []);
        setMeta(res.data?.meta?.pagination || res.data?.meta || {});
      })
      .catch(() => {
        setError(true);
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [sortBy, sortOrder, location.search]);

  useEffect(() => {
    fetchList({});
  }, [fetchList]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    history.replace({ pathname: location.pathname, search: '?page=1' });
  };

  const handlePageChange = (e, { activePage }) => {
    history.push({ pathname: location.pathname, search: `?page=${activePage}` });
    fetchList({ page: activePage });
  };

  const handleSort = (col) => {
    const nextOrder = sortBy === col && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(col);
    setSortOrder(nextOrder);
    fetchList({ sort_by: col, sort_order: nextOrder, page: 1 });
    history.replace({ pathname: location.pathname, search: '?page=1' });
  };

  const applyFilters = (filtersOverride) => {
    history.replace({ pathname: location.pathname, search: '?page=1' });
    fetchList({ page: 1, ...(filtersOverride ? { filtersOverride } : {}) });
  };

  const handleDelete = (item) => {
    const id = item.idx || item.id;
    if (!id) return;
    swal({
      title: 'Are you sure?',
      text: 'This group trip will be removed.',
      icon: 'warning',
      buttons: true,
      dangerMode: true
    }).then((ok) => {
      if (!ok) return;
      setDeletingId(id);
      deleteGroupTour(id)
        .then(() => {
          swal('Deleted', { icon: 'success' });
          fetchList({});
        })
        .catch((e) => {
          const msg = (e.response && e.response.data && (e.response.data.errors || e.response.data.message)) || 'Delete failed';
          swal('Error', Array.isArray(msg) ? msg.join(' ') : msg, 'error');
        })
        .finally(() => setDeletingId(null));
    });
  };

  const formatDate = (d) => (d ? moment(d).format('D MMM YYYY') : '—');
  const capacityStr = (min, max) => (max != null ? `${min}–${max}` : `${min}+`);

  const pag = meta.pagination || meta || {};
  const totalEntries = pag.total_entries ?? pag.total_count ?? list.length;
  const currentPage = parseInt(pag.current_page || 1, 10);
  const totalPages = Math.max(1, parseInt(pag.total_pages || 1, 10));
  const from = list.length === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const to = (currentPage - 1) * PER_PAGE + list.length;

  const SortableTh = ({ col, label }) => (
    <th
      className="cursor-pointer"
      onClick={() => handleSort(col)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title={`Sort by ${label}`}
    >
      {label}
      {sortBy === col && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  return (
    <div className="container py-4">
      <Link to="/partner" className="btn btn-outline-secondary btn-sm mb-3">← Back to Dashboard</Link>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
        <div>
          <h2 className="mb-0">My Group Trips</h2>
          <p className="text-muted small mb-0 mt-1">Edit and Delete are only available when no one has registered. Recurring: use Stop batch / Stop series from the trip detail if needed.</p>
        </div>
        <Link to="/partner/group-trips/new" className="btn btn-primary">➕ Add Group Trip</Link>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex flex-wrap align-items-end gap-2">
            <div>
              <label className="small text-muted d-block">Title</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onBlur={applyFilters}
                style={{ width: 160 }}
              />
            </div>
            <div>
              <label className="small text-muted d-block">Start date from</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                onBlur={applyFilters}
                style={{ width: 140 }}
              />
            </div>
            <div>
              <label className="small text-muted d-block">Start date to</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                onBlur={applyFilters}
                style={{ width: 140 }}
              />
            </div>
            <div>
              <label className="small text-muted d-block">Status</label>
              <select
                className="form-control form-control-sm"
                value={filters.status}
                onChange={(e) => { const v = e.target.value; handleFilterChange('status', v); applyFilters({ status: v }); }}
                style={{ width: 140 }}
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="group_formed">Group formed</option>
              </select>
            </div>
            <div>
              <label className="small text-muted d-block">Published</label>
              <select
                className="form-control form-control-sm"
                value={filters.published}
                onChange={(e) => { const v = e.target.value; handleFilterChange('published', v); applyFilters({ published: v }); }}
                style={{ width: 100 }}
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning">
          Could not load list. <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => fetchList({})}>Retry</button>
        </div>
      )}

      {loading && list.length === 0 && (
        <p className="text-muted">Loading…</p>
      )}

      {!loading && !error && list.length === 0 && (
        <div className="card">
          <div className="card-body text-center py-5">
            <p className="text-muted mb-0">{EMPTY_MSG}</p>
            <Link to="/partner/group-trips/new" className="btn btn-primary mt-3">Add Group Trip</Link>
          </div>
        </div>
      )}

      {!loading && list.length > 0 && (
        <>
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="thead-light">
                    <tr>
                      <SortableTh col="name" label="Title" />
                      <SortableTh col="start_date" label="Start date" />
                      <th>End date</th>
                      <th>Price per person</th>
                      <th>Seats / capacity</th>
                      <th>Cutoff (days)</th>
                      <th>Recurrence (days)</th>
                      <SortableTh col="published" label="Published" />
                      <th>Status</th>
                      <SortableTh col="created_at" label="Created at" />
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((t) => (
                      <tr key={t.idx || t.id}>
                        <td>{t.name || '—'}</td>
                        <td>{formatDate(t.start_date)}</td>
                        <td>{formatDate(t.end_date)}</td>
                        <td>{t.vendor_total_price != null ? `${Number(t.vendor_total_price).toLocaleString()} ${t.currency_code || 'NPR'}` : '—'}</td>
                        <td>{capacityStr(t.min_group_size, t.max_group_size)}</td>
                        <td>{t.cutoff_days_before_start != null ? t.cutoff_days_before_start : '—'}</td>
                        <td>{t.recurrence_days != null ? t.recurrence_days : '—'}</td>
                        <td>{t.published ? 'Yes' : 'No'}</td>
                        <td>
                          {t.group_formed ? 'Group formed' : 'Open'}
                          {t.template_id && <span className="ml-1 badge badge-info" title="Recurring: use Edit to stop this batch or series">Recurring</span>}
                        </td>
                        <td>{t.created_at ? moment(t.created_at).format('D MMM YYYY') : '—'}</td>
                        <td className="text-right">
                          {t.has_any_booking ? (
                            <span className="text-muted small align-middle" title="Not available once anyone has registered">Edit / Delete locked</span>
                          ) : (
                            <>
                              <Link to={`/partner/group-trips/edit/${t.idx || t.id}`} className="btn btn-sm btn-outline-primary mr-1">✏ Edit</Link>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(t)}
                                disabled={deletingId === (t.idx || t.id)}
                              >
                                🗑 Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
            <span className="text-muted small">Showing {from}–{to} of {totalEntries} records</span>
            {totalPages > 1 && (
              <Pagination
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                firstItem={null}
                lastItem={null}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
